import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { usePlanRunStore } from '@/stores/trajectory/plan-run-store';
import { useOrganizationStore } from '@/stores/organization/organization-store';
import { PlanRunPhase, PlanRunProgressSteps, planRunStepIndexFor } from '@/constants/plan-run-phase';

/**
 * De teller loopt per seconde, terwijl de poller de stand elke vijf seconden ophaalt. Zonder eigen tik zou
 * de verstreken tijd met sprongen van vijf seconden verspringen, en dat leest als een hangende run.
 */
const TICK_INTERVAL_MS = 1_000;

/**
 * De voortgang van de lopende plan-run: in welke stap hij zit en hoe lang hij al bezig is.
 *
 * Bewust géén ETA over de hele run. APE's statusantwoord bevat geen percentage, geen iteratieteller en geen
 * resterende tijd, en de looptijd is niet te voorspellen: het zoekbudget geldt alleen voor het oplossen, en
 * de afstandsmatrix, het uitlezen en ons eigen verwerken komen daar bovenop. Wat we wél exact weten is hoe
 * lang de run loopt, en dat is dus wat er staat.
 */
export function usePlanRunProgress() {
  const planRunStore = usePlanRunStore();
  const organizationStore = useOrganizationStore();

  const now = ref(Date.now());
  let timer: ReturnType<typeof setInterval> | null = null;

  onMounted(() => {
    timer = setInterval(() => {
      now.value = Date.now();
    }, TICK_INTERVAL_MS);
  });

  onBeforeUnmount(() => {
    if (timer !== null) {
      clearInterval(timer);
      timer = null;
    }
  });

  /**
   * De hoogst bereikte stap. De balk mag niet terugvallen: APE laat de fase leeg zolang de job in zijn
   * wachtrij staat en tussen twee fasen in, en een poll kan een oudere stand meebrengen.
   */
  const maxStepIndex = ref(0);

  watch(
    () => [planRunStore.activeRun?.id ?? null, planRunStore.activeRun?.phase ?? null] as const,
    ([id, phase], previous) => {
      if (id === null) {
        return;
      }

      // Een volgende run begint weer vooraan.
      if (previous !== undefined && previous[0] !== id) {
        maxStepIndex.value = 0;
      }

      maxStepIndex.value = Math.max(maxStepIndex.value, planRunStepIndexFor(phase));
    },
    { immediate: true },
  );

  const step = computed(() => PlanRunProgressSteps[maxStepIndex.value] ?? PlanRunProgressSteps[0]);

  /** Waar de volgende stap begint; de meeloop binnen een stap mag daar nooit aan raken. */
  const nextStepPercentage = computed(
    () => PlanRunProgressSteps[maxStepIndex.value + 1]?.percentage ?? 100,
  );

  /**
   * Serverklok. Het verschil met de browserklok kan minuten zijn, en zowel startedAt als phaseSince komen
   * van de server.
   */
  const serverNow = computed(() => now.value + planRunStore.serverTimeOffset);

  /**
   * Verstreken tijd van de hele run. De ondergrens van nul is nodig omdat seedActiveRun startedAt op de
   * browserklok zet: met een offset erbij kan dat even in de toekomst liggen.
   */
  const elapsedMs = computed(() => {
    const run = planRunStore.activeRun;

    return run === null ? 0 : Math.max(0, serverNow.value - run.startedAt);
  });

  /**
   * Binnen de oplosfase loopt de balk mee op het zoekbudget.
   *
   * Dat kan alleen daar: APE zet de zoektocht met gerichte lokale zoekmethode weg, die niet vroeg stopt maar
   * het volledige budget benut — dus die fase duurt in het normale geval precies de ingestelde zoektijd. Loopt
   * hij toch uit (APE mag bij de rijtijdenwet eerst aftasten, en escaleert wanneer er geen enkele auto
   * geplaatst raakte), dan blijft de balk staan tot de volgende fase komt in plaats van vooruit te lopen op
   * iets dat nog niet klaar is.
   *
   * Het budget komt uit de organisatie-instellingen, niet van de run zelf: dat is dezelfde waarde die de
   * orkestratie meestuurt. Wordt de instelling midden in een run gewijzigd, dan loopt de balk van dát moment
   * op de nieuwe waarde — zichtbaar noch schadelijk, en het scheelt het zoekbudget per run bijhouden.
   */
  const percentage = computed(() => {
    const run = planRunStore.activeRun;
    const budgetSeconds = organizationStore.organizationSettings.apeSettings?.searchTimeLimitSeconds ?? 0;

    // Alleen tijdens de fase zelf, niet terwijl de weergave op deze stap blijft staan omdat APE even geen
    // fase meldt: phaseSince hoort dan bij een andere fase.
    if (
      run === null ||
      run.phase !== PlanRunPhase.Solving ||
      step.value.phase !== PlanRunPhase.Solving ||
      run.phaseSince === null ||
      budgetSeconds <= 0
    ) {
      return step.value.percentage;
    }

    const fraction = Math.min(1, Math.max(0, serverNow.value - run.phaseSince) / (budgetSeconds * 1_000));
    // Eén procentpunt onder de volgende stap: die is voor het bereiken van die stap, niet voor het naderen.
    const ceiling = nextStepPercentage.value - 1;

    return Math.round(step.value.percentage + (ceiling - step.value.percentage) * fraction);
  });

  return {
    percentage,
    phaseLabelKey: computed(() => step.value.labelKey),
    elapsedMinutes: computed(() => Math.floor(elapsedMs.value / 60_000)),
    elapsedSeconds: computed(() => Math.floor((elapsedMs.value % 60_000) / 1_000)),
  };
}
